import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import type { DateRange } from '@mui/x-date-pickers-pro';
import is from '@sindresorhus/is';
import type { ChangeEventHandler, FunctionComponent } from 'react';
import { Fragment, useContext, useState } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';
import type {
    FilterState,
    FilterStateBoolean,
    FilterStateDate,
    FilterStateInteger,
    FilterStatePercentage,
    FilterStateSkills,
    FilterStateString,
    FilterStateUser,
    FilterWithFilterStates,
} from '~/shared/data-contract/filter-state';
import {
    filterStateComponentMultiChipSelectLiteralSchema,
    filterStatePercentageRangeSchema,
    filterStateSkillSchema,
} from '~/shared/data-contract/filter-state';
import type { SkillShort } from '~/shared/data-contract/skill';

import { DateRangePicker } from '../../../design/components/form-fields/DateRangePicker';
import { Select } from '../../../design/components/form-fields/Select';
import { SkillSearch } from '../form/skills-field/SkillsSearch';
import { ReactHookFormAutocompleteField } from '../react-hook-form-fields/ReactHookFormAutocompleteField';
import { ReactHookFormMultiChipSelectorField } from '../react-hook-form-fields/ReactHookFormMultiChipSelectorField';
import { SkillList } from './SkillList';

type FieldName = `${number}.filterStates.${number}.value`;

type FilterInput<T extends FilterState = FilterState> = {
    readonly control: Control<readonly FilterWithFilterStates[]>;
    readonly fieldName: FieldName;
    readonly filterState: T;
};

const BooleanField: FunctionComponent<FilterInput<FilterStateBoolean>> = ({ control, fieldName, filterState }) => {
    const { translate } = useContext(ApplicationContext);
    const [localValue, setLocalValue] = useState<boolean | undefined>(
        is.boolean(filterState.value) ? filterState.value : undefined,
    );

    return (
        <Controller
            control={control}
            name={fieldName}
            render={({ field, fieldState }) => {
                const value = field.value ?? localValue;
                const displayValue = is.boolean(value) ? String(value) : undefined;

                return (
                    <Select
                        helperText={fieldState.error?.message}
                        isError={is.nonEmptyString(fieldState.error?.message)}
                        label={filterState.label}
                        name={field.name}
                        options={[
                            {
                                key: 'none',
                                label: translate('common.no-selection'),
                                value: '',
                            },
                            {
                                key: 'true',
                                label: translate('common.yes'),
                                value: 'true',
                            },
                            {
                                key: 'false',
                                label: translate('common.no'),
                                value: 'false',
                            },
                        ]}
                        selectedValue={displayValue}
                        onChange={(e) => {
                            const newValue = is.emptyString(e.target.value) ? undefined : e.target.value === 'true';
                            setLocalValue(newValue);
                            field.onChange(newValue);
                        }}
                    />
                );
            }}
        />
    );
};

const isStringArray = (value: unknown | readonly unknown[]): value is readonly string[] => is.array(value, is.string);

const SkillField: FunctionComponent<FilterInput<FilterStateSkills>> = ({ control, fieldName, filterState }) => {
    const [localSkillIds, setLocalSkillIds] = useState<readonly string[]>(
        isStringArray(filterState.value) ? filterState.value : [],
    );

    return (
        <Controller
            control={control}
            name={fieldName}
            render={({ field }) => {
                const value = field.value ?? localSkillIds;

                const onAddSkill = (skill: SkillShort): void => {
                    const current = isStringArray(value) ? value : [];
                    const updated = [...current, skill.id];
                    setLocalSkillIds(updated);
                    field.onChange(updated);
                };
                const onRemoveSkill = (skillId: string): void => {
                    if (isStringArray(value)) {
                        const updated = value.filter((id) => id !== skillId);
                        setLocalSkillIds(updated);
                        field.onChange(updated);
                    }
                };

                const skillIdsParsed = filterStateSkillSchema.shape.value
                    .transform((skillIds) => {
                        return Maybe.of(skillIds);
                    })
                    .safeParse(value);
                const skillIds = skillIdsParsed.success ? skillIdsParsed.data.unwrapOr([]) : [];

                return (
                    <Fragment>
                        {is.nonEmptyArray(skillIds) && (
                            <SkillList label={filterState.label} skillIds={skillIds} onRemoveSkill={onRemoveSkill} />
                        )}
                        <SkillSearch excludedSkillIds={skillIds} onAddSkill={onAddSkill} />
                    </Fragment>
                );
            }}
        />
    );
};

const StringField: FunctionComponent<FilterInput<FilterStateString>> = ({ control, fieldName, filterState }) => {
    const { component, label, options } = filterState;

    if (is.emptyArray(options)) {
        return null;
    }

    if (component === filterStateComponentMultiChipSelectLiteralSchema.value) {
        return (
            <ReactHookFormMultiChipSelectorField control={control} label={label} name={fieldName} options={options} />
        );
    }

    return <ReactHookFormAutocompleteField control={control} label={label} name={fieldName} options={options} />;
};

const UserField: FunctionComponent<FilterInput<FilterStateUser>> = ({ control, fieldName, filterState }) => (
    <ReactHookFormAutocompleteField
        control={control}
        label={filterState.label}
        name={fieldName}
        options={filterState.options}
    />
);

const IntegerField: FunctionComponent<FilterInput<FilterStateInteger>> = ({ control, fieldName, filterState }) => {
    const { component, label, options } = filterState;

    if (is.emptyArray(options)) {
        return null;
    }

    if (component === filterStateComponentMultiChipSelectLiteralSchema.value) {
        return (
            <ReactHookFormMultiChipSelectorField control={control} label={label} name={fieldName} options={options} />
        );
    }

    return <ReactHookFormAutocompleteField control={control} label={label} name={fieldName} options={options} />;
};

const DateField: FunctionComponent<FilterInput<FilterStateDate>> = ({ control, fieldName, filterState }) => {
    const { translate } = useContext(ApplicationContext);
    const initialDate: DateRange<Date> = is.nonEmptyArray(filterState.value)
        ? [
              is.date(filterState.value[0]) ? filterState.value[0] : null,
              is.date(filterState.value[1]) ? filterState.value[1] : null,
          ]
        : [null, null];
    const [localDateValue, setLocalDateValue] = useState<DateRange<Date>>(initialDate);

    return (
        <Controller
            control={control}
            name={fieldName}
            render={({ field }) => {
                const { value } = field;

                const dateValue: DateRange<Date> = is.nonEmptyArray(value)
                    ? [is.date(value[0]) ? value[0] : null, is.date(value[1]) ? value[1] : null]
                    : localDateValue;

                return (
                    <DateRangePicker
                        label={filterState.label}
                        labelEnd={translate('common.end')}
                        labelStart={translate('common.start')}
                        value={dateValue}
                        onChange={(newValue) => {
                            setLocalDateValue(newValue);
                            field.onChange(newValue);
                        }}
                    />
                );
            }}
        />
    );
};

const RangeSliderTextField: FunctionComponent<{
    readonly label: string;
    readonly onChange: ChangeEventHandler<HTMLTextAreaElement>;
    readonly value: string;
}> = (field) => {
    const { label, value } = field;

    return (
        <TextField
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: 100, min: 0, step: 1 }}
            label={label}
            sx={{ gap: 3, height: '40px', width: '80px' }}
            type="number"
            value={value}
            variant="outlined"
            onChange={field.onChange}
        />
    );
};

const RangeSlider: FunctionComponent<FilterInput<FilterStatePercentage>> = ({ control, fieldName, filterState }) => {
    const initialRange = filterStatePercentageRangeSchema.safeParse(filterState.value);
    const [localRange, setLocalRange] = useState<[number, number]>(initialRange.success ? initialRange.data : [0, 1]);

    return (
        <Controller
            control={control}
            name={fieldName}
            render={({ field }) => {
                const { value } = field;
                const percentageRange = filterStatePercentageRangeSchema.safeParse(value);
                const [start, end] = percentageRange.success ? percentageRange.data : localRange;

                return (
                    <Fragment>
                        <Slider
                            marks={[
                                {
                                    label: '0 %',
                                    value: 0,
                                },
                                {
                                    value: 0.25,
                                },
                                {
                                    value: 0.5,
                                },
                                {
                                    value: 0.75,
                                },
                                {
                                    label: '100%',
                                    value: 1,
                                },
                            ]}
                            max={1}
                            min={0}
                            step={0.01}
                            sx={{
                                '& .MuiSlider-markLabel[data-index="0"]': {
                                    transform: 'translateX(0%)',
                                },
                                '& .MuiSlider-markLabel[data-index="4"]': {
                                    transform: 'translateX(-100%)',
                                },
                            }}
                            value={[start, end]}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${Math.round(value * 100)} %`}
                            onChange={(_event, newValue) => {
                                if (!is.array(newValue)) return;
                                const [low, high] = newValue;
                                if (!is.number(low) || !is.number(high)) return;
                                const range: [number, number] = [low, high];
                                setLocalRange(range);
                                field.onChange(range);
                            }}
                        />
                        <Grid alignItems="center" container={true} flexDirection="row" gap={3} justifyContent="center">
                            <RangeSliderTextField
                                label="Min in %"
                                value={String(Math.round(start * 100))}
                                onChange={(event) => {
                                    const newRange: [number, number] = [Number(event.target.value) / 100, end];
                                    setLocalRange(newRange);
                                    field.onChange(newRange);
                                }}
                            />
                            -
                            <RangeSliderTextField
                                label="Max in %"
                                value={String(Math.round(end * 100))}
                                onChange={(event) => {
                                    const newRange: [number, number] = [start, Number(event.target.value) / 100];
                                    setLocalRange(newRange);
                                    field.onChange(newRange);
                                }}
                            />
                        </Grid>
                    </Fragment>
                );
            }}
        />
    );
};

export const FilterInput: FunctionComponent<FilterInput> = ({ control, fieldName, filterState }) => {
    const { type } = filterState;

    if (type === 'boolean') {
        return <BooleanField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'string') {
        return <StringField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'user' && is.nonEmptyArray(filterState.options)) {
        return <UserField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'integer') {
        return <IntegerField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'date') {
        return <DateField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'skill') {
        return <SkillField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'percent') {
        return <RangeSlider control={control} fieldName={fieldName} filterState={filterState} />;
    }

    return null;
};
